// components/SelfPaced/QuizBuilder.js
// Admin quiz builder — supports MCQ, multi-select, true/false, short text.
// Themed, minimal UI matching the admin course builder design.
import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import {
  Plus, Trash2, X, GripVertical,
  CheckCircle2, CircleDot, ListChecks, ToggleLeft, Type, Save,
} from 'lucide-react';
import { AppButton } from '../ui';
import { createQuiz, updateQuiz, getQuiz } from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

// ── Question type config ─────────────────────────────────────────────────────
const QUESTION_TYPES = [
  { key: 'mcq',          label: 'Multiple Choice', icon: CircleDot,  desc: 'Single correct answer' },
  { key: 'multi_select', label: 'Multi Select',    icon: ListChecks, desc: 'Multiple correct answers' },
  { key: 'true_false',   label: 'True / False',    icon: ToggleLeft, desc: 'True or false only' },
  { key: 'short_text',   label: 'Short Answer',    icon: Type,       desc: 'Typed text answer' },
];

// ── Helper: empty question factory ───────────────────────────────────────────
const emptyQuestion = (questionType = 'mcq') => {
  const base = {
    _tempId: Date.now() + Math.random(),
    question: '',
    questionType,
    explanation: '',
    marks: 1,
  };

  if (questionType === 'mcq') {
    return { ...base, options: ['', '', '', ''], correctIndex: 0 };
  }
  if (questionType === 'multi_select') {
    return { ...base, options: ['', '', '', ''], correctIndices: [0] };
  }
  if (questionType === 'true_false') {
    return { ...base, options: ['True', 'False'], correctIndex: 0 };
  }
  if (questionType === 'short_text') {
    return { ...base, options: [], correctText: '' };
  }
  return base;
};

// ═════════════════════════════════════════════════════════════════════════════
// QuizBuilder Component
// ═════════════════════════════════════════════════════════════════════════════
const QuizBuilder = forwardRef(({
  quizId,
  moduleId: propModuleId,
  courseId,
  onSaved,
  onClose,
  setToast,
  compact = false, // compact mode for embedding inside AddLessonModal
}, ref) => {
  const isEdit = !!quizId;
  const [title, setTitle] = useState('');
  const [passingScore, setPassingScore] = useState(60);
  const [timeLimit, setTimeLimit] = useState('');
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [isPublished, setIsPublished] = useState(true);
  const [questions, setQuestions] = useState([emptyQuestion('mcq')]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!quizId);
  const [addingType, setAddingType] = useState(null); // show type picker

  // ── Load existing quiz in edit ──
  useEffect(() => {
    if (!quizId) return;
    (async () => {
      try {
        const res = await getQuiz(quizId);
        const q = res.data?.data || res.data;
        setTitle(q.title || '');
        setPassingScore(q.passingScore ?? 60);
        setTimeLimit(q.timeLimit || '');
        setMaxAttempts(q.maxAttempts ?? 3);
        setIsPublished(q.isPublished ?? true);
        setQuestions(
          (q.questions || []).map((qu, i) => ({
            _tempId: i,
            question: qu.question || '',
            questionType: qu.questionType || 'mcq',
            options: (qu.options || []).map(o => typeof o === 'string' ? o : o.text || ''),
            correctIndex: qu.correctIndex ?? 0,
            correctIndices: qu.correctIndices || [],
            correctText: qu.correctText || '',
            explanation: qu.explanation || '',
            marks: qu.marks ?? 1,
          }))
        );
      } catch {
        setToast?.({ show: true, message: 'Failed to load quiz.', type: 'error' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [quizId]);

  // ── Question CRUD ──
  const addQuestion = (questionType) => {
    setQuestions(p => [...p, emptyQuestion(questionType)]);
    setAddingType(null);
  };

  const removeQuestion = (idx) => {
    if (questions.length <= 1) return;
    setQuestions(p => p.filter((_, i) => i !== idx));
  };

  const updateQuestion = useCallback((idx, field, value) => {
    setQuestions(p => p.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  }, []);

  const updateOption = useCallback((qIdx, oIdx, value) => {
    setQuestions(p => p.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...q.options];
      opts[oIdx] = value;
      return { ...q, options: opts };
    }));
  }, []);

  const addOption = useCallback((qIdx) => {
    setQuestions(p => p.map((q, i) =>
      i === qIdx ? { ...q, options: [...q.options, ''] } : q
    ));
  }, []);

  const removeOption = useCallback((qIdx, oIdx) => {
    setQuestions(p => p.map((q, i) => {
      if (i !== qIdx || q.options.length <= 2) return q;
      const opts = q.options.filter((_, oi) => oi !== oIdx);
      let ci = q.correctIndex;
      if (ci !== undefined && ci !== null) {
        ci = ci >= opts.length ? 0 : (ci > oIdx ? ci - 1 : ci);
      }
      let cis = (q.correctIndices || []).filter(x => x !== oIdx).map(x => x > oIdx ? x - 1 : x);
      return { ...q, options: opts, correctIndex: ci, correctIndices: cis };
    }));
  }, []);

  const toggleMultiSelectCorrect = useCallback((qIdx, oIdx) => {
    setQuestions(p => p.map((q, i) => {
      if (i !== qIdx) return q;
      const cis = q.correctIndices || [];
      const next = cis.includes(oIdx)
        ? cis.filter(x => x !== oIdx)
        : [...cis, oIdx];
      return { ...q, correctIndices: next };
    }));
  }, []);

  // ── Validation ──
  const validate = () => {
    if (!title.trim()) {
      setToast?.({ show: true, message: 'Quiz title is required.', type: 'error' });
      return false;
    }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question.trim()) {
        setToast?.({ show: true, message: `Question ${i + 1}: Please enter question text.`, type: 'error' });
        return false;
      }
      if (q.questionType === 'mcq' || q.questionType === 'multi_select' || q.questionType === 'true_false') {
        if (q.options.some(o => !o.trim())) {
          setToast?.({ show: true, message: `Question ${i + 1}: All options must have text.`, type: 'error' });
          return false;
        }
      }
      if (q.questionType === 'multi_select' && (!q.correctIndices || q.correctIndices.length === 0)) {
        setToast?.({ show: true, message: `Question ${i + 1}: Select at least one correct answer.`, type: 'error' });
        return false;
      }
      if (q.questionType === 'short_text' && !q.correctText?.trim()) {
        setToast?.({ show: true, message: `Question ${i + 1}: Please enter the correct answer text.`, type: 'error' });
        return false;
      }
    }
    return true;
  };

  // ── Build payload ──
  const buildPayload = () => ({
    title: title.trim(),
    passingScore: Number(passingScore),
    timeLimit: timeLimit ? Number(timeLimit) : 0,
    maxAttempts: maxAttempts ? Number(maxAttempts) : 3,
    isPublished,
    courseId,
    moduleId: propModuleId || undefined,
    questions: questions.map(q => {
      const base = {
        question: q.question.trim(),
        questionType: q.questionType,
        explanation: (q.explanation || '').trim(),
        marks: q.marks || 1,
      };

      if (q.questionType === 'mcq' || q.questionType === 'true_false') {
        return { ...base, options: q.options.map(o => o.trim()), correctIndex: q.correctIndex };
      }
      if (q.questionType === 'multi_select') {
        return { ...base, options: q.options.map(o => o.trim()), correctIndices: q.correctIndices || [] };
      }
      if (q.questionType === 'short_text') {
        return { ...base, options: [], correctText: (q.correctText || '').trim() };
      }
      return base;
    }),
  });

  // ── Submit ──
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validate()) return null;
    setSaving(true);
    try {
      const payload = buildPayload();

      let result;
      if (isEdit) {
        result = await updateQuiz(quizId, payload);
        setToast?.({ show: true, message: 'Quiz updated!', type: 'success' });
      } else {
        result = await createQuiz(payload);
        setToast?.({ show: true, message: 'Quiz created!', type: 'success' });
      }

      const quizData = result?.data?.data || result?.data;
      onSaved?.(quizData);
      if (!compact) onClose?.();
      return quizData;
    } catch (err) {
      setToast?.({
        show: true,
        message: err?.response?.data?.message || 'Failed to save quiz.',
        type: 'error',
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Expose submit method for parent (compact mode in AddLessonModal)
  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
    validate,
  }));

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <span className="ms-2">Loading quiz...</span>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className={`qb ${compact ? 'qb--compact' : ''}`}>
      {/* ── Quiz Settings ── */}
      <div className="qb-settings">
        <div className="qb-settings__row">
          <div className="qb-settings__field qb-settings__field--title">
            <label className="sp-field-label">Quiz Title <span className="text-danger">*</span></label>
            <Form.Control
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Module 1 Assessment"
              disabled={saving}
              size="sm"
            />
          </div>
          <div className="qb-settings__field">
            <label className="sp-field-label">Pass %</label>
            <Form.Control
              type="number"
              min={0}
              max={100}
              value={passingScore}
              onChange={(e) => setPassingScore(e.target.value)}
              disabled={saving}
              size="sm"
            />
          </div>
          <div className="qb-settings__field">
            <label className="sp-field-label">Time (min)</label>
            <Form.Control
              type="number"
              min={0}
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              placeholder="0 = none"
              disabled={saving}
              size="sm"
            />
          </div>
          <div className="qb-settings__field">
            <label className="sp-field-label">Max Tries</label>
            <Form.Control
              type="number"
              min={0}
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(e.target.value)}
              placeholder="0 = ∞"
              disabled={saving}
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* ── Questions ── */}
      <div className="qb-questions">
        {questions.map((q, qi) => (
          <QuestionCard
            key={q._tempId ?? qi}
            q={q}
            qi={qi}
            saving={saving}
            total={questions.length}
            onRemove={() => removeQuestion(qi)}
            onUpdateQuestion={updateQuestion}
            onUpdateOption={updateOption}
            onAddOption={addOption}
            onRemoveOption={removeOption}
            onToggleMultiCorrect={toggleMultiSelectCorrect}
          />
        ))}
      </div>

      {/* ── Add Question ── */}
      <div className="qb-add-question">
        {addingType ? (
          <div className="qb-type-picker">
            <p className="qb-type-picker__label">Choose question type:</p>
            <div className="qb-type-picker__grid">
              {QUESTION_TYPES.map(({ key, label, icon: Icon, desc }) => (
                <button
                  key={key}
                  type="button"
                  className="qb-type-card"
                  onClick={() => addQuestion(key)}
                  disabled={saving}
                >
                  <Icon size={20} />
                  <span className="qb-type-card__label">{label}</span>
                  <span className="qb-type-card__desc">{desc}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="qb-type-picker__cancel"
              onClick={() => setAddingType(null)}
            >
              Cancel
            </button>
          </div>
        ) : (
          <AppButton
            variant="outline"
            size="sm"
            onClick={() => setAddingType(true)}
            disabled={saving}
          >
            <Plus size={14} /> Add Question
          </AppButton>
        )}
      </div>

      {/* ── Footer (standalone mode) ── */}
      {!compact && (
        <div className="qb-footer">
          {onClose && (
            <AppButton variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </AppButton>
          )}
          <AppButton onClick={handleSubmit} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving...' : isEdit ? 'Update Quiz' : 'Create Quiz'}
          </AppButton>
        </div>
      )}

      {/* Compact mode — just show count, parent calls ref.submit() */}
      {compact && (
        <div className="qb-footer qb-footer--compact">
          <span className="qb-footer__count">
            {questions.length} question{questions.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// QuestionCard Sub-Component
// ═════════════════════════════════════════════════════════════════════════════
const QuestionCard = ({
  q, qi, saving, total,
  onRemove, onUpdateQuestion, onUpdateOption,
  onAddOption, onRemoveOption, onToggleMultiCorrect,
}) => {
  const typeCfg = QUESTION_TYPES.find(t => t.key === q.questionType) || QUESTION_TYPES[0];
  const TypeIcon = typeCfg.icon;

  return (
    <div className="qb-question">
      {/* Header */}
      <div className="qb-question__header">
        <GripVertical size={14} className="qb-question__grip" />
        <span className="qb-question__number">Q{qi + 1}</span>
        <span className="qb-question__type-badge">
          <TypeIcon size={12} />
          {typeCfg.label}
        </span>
        <span className="qb-question__marks">
          <Form.Control
            type="number"
            min={1}
            value={q.marks}
            onChange={(e) => onUpdateQuestion(qi, 'marks', Number(e.target.value) || 1)}
            className="qb-question__marks-input"
            disabled={saving}
            size="sm"
          />
          <span className="qb-question__marks-label">pts</span>
        </span>
        <button
          type="button"
          className="qb-question__remove"
          onClick={onRemove}
          disabled={total <= 1 || saving}
          title="Remove question"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Question Text */}
      <div className="qb-question__body">
        <Form.Control
          as="textarea"
          rows={2}
          value={q.question}
          onChange={(e) => onUpdateQuestion(qi, 'question', e.target.value)}
          placeholder="Enter your question..."
          disabled={saving}
          className="qb-question__text"
        />

        {/* ── MCQ Options ── */}
        {q.questionType === 'mcq' && (
          <div className="qb-options">
            {q.options.map((opt, oi) => (
              <div key={oi} className="qb-option">
                <Form.Check
                  type="radio"
                  name={`correct-${qi}`}
                  checked={q.correctIndex === oi}
                  onChange={() => onUpdateQuestion(qi, 'correctIndex', oi)}
                  className="qb-option__radio"
                  disabled={saving}
                />
                <Form.Control
                  size="sm"
                  value={opt}
                  onChange={(e) => onUpdateOption(qi, oi, e.target.value)}
                  placeholder={`Option ${oi + 1}`}
                  disabled={saving}
                  className="qb-option__input"
                />
                {q.correctIndex === oi && (
                  <CheckCircle2 size={16} className="qb-option__correct-icon" />
                )}
                {q.options.length > 2 && (
                  <button
                    type="button"
                    className="qb-option__remove"
                    onClick={() => onRemoveOption(qi, oi)}
                    disabled={saving}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="qb-option__add-btn" onClick={() => onAddOption(qi)} disabled={saving}>
              <Plus size={12} /> Add Option
            </button>
          </div>
        )}

        {/* ── Multi-Select Options ── */}
        {q.questionType === 'multi_select' && (
          <div className="qb-options">
            <p className="qb-options__hint">Select all correct answers:</p>
            {q.options.map((opt, oi) => {
              const isCorrect = (q.correctIndices || []).includes(oi);
              return (
                <div key={oi} className="qb-option">
                  <Form.Check
                    type="checkbox"
                    checked={isCorrect}
                    onChange={() => onToggleMultiCorrect(qi, oi)}
                    className="qb-option__radio"
                    disabled={saving}
                  />
                  <Form.Control
                    size="sm"
                    value={opt}
                    onChange={(e) => onUpdateOption(qi, oi, e.target.value)}
                    placeholder={`Option ${oi + 1}`}
                    disabled={saving}
                    className="qb-option__input"
                  />
                  {isCorrect && (
                    <CheckCircle2 size={16} className="qb-option__correct-icon" />
                  )}
                  {q.options.length > 2 && (
                    <button
                      type="button"
                      className="qb-option__remove"
                      onClick={() => onRemoveOption(qi, oi)}
                      disabled={saving}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
            <button type="button" className="qb-option__add-btn" onClick={() => onAddOption(qi)} disabled={saving}>
              <Plus size={12} /> Add Option
            </button>
          </div>
        )}

        {/* ── True / False ── */}
        {q.questionType === 'true_false' && (
          <div className="qb-options qb-options--tf">
            {['True', 'False'].map((label, oi) => (
              <label
                key={oi}
                className={`qb-tf-option ${q.correctIndex === oi ? 'qb-tf-option--correct' : ''}`}
              >
                <Form.Check
                  type="radio"
                  name={`tf-${qi}`}
                  checked={q.correctIndex === oi}
                  onChange={() => onUpdateQuestion(qi, 'correctIndex', oi)}
                  disabled={saving}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        )}

        {/* ── Short Text ── */}
        {q.questionType === 'short_text' && (
          <div className="qb-short-text">
            <label className="sp-field-label">
              Correct Answer <span className="text-danger">*</span>
            </label>
            <Form.Control
              size="sm"
              value={q.correctText || ''}
              onChange={(e) => onUpdateQuestion(qi, 'correctText', e.target.value)}
              placeholder="Type the expected answer (case-insensitive)"
              disabled={saving}
            />
            <p className="qb-short-text__hint">
              Student's answer will be matched case-insensitively.
            </p>
          </div>
        )}

        {/* Explanation */}
        <div className="qb-explanation">
          <label className="sp-field-label">Explanation (optional)</label>
          <Form.Control
            size="sm"
            value={q.explanation}
            onChange={(e) => onUpdateQuestion(qi, 'explanation', e.target.value)}
            placeholder="Explain the correct answer"
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
};

QuizBuilder.displayName = 'QuizBuilder';
export default QuizBuilder;
